import { Link } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import React from "react";
import type {
  PendingDocShareProjectDraftClient,
  PendingDocShareProjectDraftNewContact,
} from "./projectDraft.js";

interface Props {
  clientState: PendingDocShareProjectDraftClient;
  draftClient: PendingDocShareProjectDraftClient;
  contactCandidate: PendingDocShareProjectDraftNewContact | null;
  onChooseExisting: () => void;
  onChooseNew: () => void;
  disabled?: boolean;
}

// The client resolution, continuing the client field's "Taken from the quote"
// hint: a CRM match reads as matched and offers the quote's own contact
// instead, the candidate chosen over it offers the match back, and an
// unmatched candidate is announced. A client the user cleared or picked
// themselves reads "Manually changed" in the hint itself, so nothing is
// rendered then.
export function ClientResolutionHint({
  clientState,
  draftClient,
  contactCandidate,
  onChooseExisting,
  onChooseNew,
  disabled = false,
}: Props) {
  const { t } = useTranslate(["ProjectCreate"]);

  // Separated like the hint's other segments ("Taken from the quote · …").
  const link = (label: string, onClick: () => void) => (
    <>
      {" · "}
      <Link
        component="button"
        type="button"
        variant="caption"
        onClick={onClick}
        disabled={disabled}
        sx={{ verticalAlign: "baseline" }}
      >
        {label}
      </Link>
    </>
  );

  if (draftClient.kind === "matched") {
    if (
      clientState.kind === "matched" &&
      clientState.crmOrganisationId === draftClient.crmOrganisationId
    ) {
      return (
        <span>
          {` · ${t("Matched automatically", { ns: "ProjectCreate" })}`}
          {contactCandidate &&
            link(t("Create new contact", { ns: "ProjectCreate" }), onChooseNew)}
        </span>
      );
    }
    if (clientState.kind === "newContact" && clientState === contactCandidate) {
      return (
        <span>
          {` · ${t("Created on submit", { ns: "ProjectCreate" })}`}
          {link(
            t("Use existing contact", { ns: "ProjectCreate" }),
            onChooseExisting
          )}
        </span>
      );
    }
    return null;
  }

  if (draftClient.kind === "newContact" && clientState === draftClient) {
    return (
      <span>
        {` · ${t("No CRM match – will be created as new contact", {
          ns: "ProjectCreate",
        })}`}
      </span>
    );
  }

  return null;
}
